import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Skeleton } from "antd";
import {
  fetchProtocolFilters,
  fetchProtocolTemplateFilters,
  type ProtocolFiltersResponse,
} from "./protocolsService";
import { useProtocolsRouter } from "../../shared/custom-hooks/useProtocolsRouter";
import {
  CheckIcon,
  CloseIcon,
  CollapseArrowIcon,
  SmallCloseIcon,
} from "./NewFiltersPanelIcons";
import { getAllLabel } from "../../shared/utils/filterLabels";
import "./NewFiltersPanel.less";

export interface CommonFiltersState {
  status: string[];
  type: string[];
  crop: string[];
  target: string[];
  country: string[];
  creator: string[];
  assignee?: string[];
}

interface CommonNewFiltersPanelProps {
  open: boolean;
  activeTab: string;
  onClose: () => void;
  onApply?: (filters: CommonFiltersState) => void;
  onCancel?: () => void;
  data: any[];
  initialFilters?: Partial<CommonFiltersState>;
}

const EMPTY_FILTERS: CommonFiltersState = {
  status: [],
  type: [],
  crop: [],
  target: [],
  country: [],
  creator: [],
  assignee: [],
};

const DEFAULT_COLLAPSED: Record<keyof CommonFiltersState, boolean> = {
  status: true,
  type: true,
  crop: true,
  target: true,
  country: true,
  creator: true,
  assignee: true,
};

const DEFAULT_TYPE_OPTIONS = ["Seeds", "CP & Biologicals", "Nutrients"];

const normalizeInitialFilters = (
  initialFilters?: Partial<CommonFiltersState>
): CommonFiltersState => ({
  ...EMPTY_FILTERS,
  ...(initialFilters ?? {}),
});

export default function CommonNewFiltersPanel({
  open,
  activeTab,
  onClose,
  onApply,
  onCancel,
  data,
  initialFilters,
}: CommonNewFiltersPanelProps): JSX.Element | null {
  const { workspaceId, orgId } = useProtocolsRouter();

  const [loadingFilters, setLoadingFilters] = useState(false);
  const [apiFilters, setApiFilters] = useState<ProtocolFiltersResponse | null>(
    null
  );

  const [localFilters, setLocalFilters] = useState<CommonFiltersState>(
    normalizeInitialFilters(initialFilters)
  );

  const [collapsed, setCollapsed] =
    useState<Record<keyof CommonFiltersState, boolean>>(DEFAULT_COLLAPSED);

  const previousOpenRef = useRef(open);
  const loadedFiltersKeyRef = useRef<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open || !orgId || !workspaceId) return;

    const isArchivedTab = activeTab === "archived";
    const isTemplatesTab = activeTab === "templates";
    const filtersKey = `${orgId}:${workspaceId}:${activeTab}`;

    if (loadedFiltersKeyRef.current === filtersKey && apiFilters) {
      return;
    }

    const loadFilters = async (): Promise<void> => {
      try {
        setLoadingFilters(true);

        const filters = isTemplatesTab
          ? await fetchProtocolTemplateFilters(orgId, workspaceId)
          : await fetchProtocolFilters(orgId, workspaceId, isArchivedTab);

        setApiFilters({
          crops: Array.isArray(filters.crops) ? filters.crops : [],
          countries: Array.isArray(filters.countries) ? filters.countries : [],
          targets: Array.isArray(filters.targets) ? filters.targets : [],
          created_by: Array.isArray(filters.created_by)
            ? filters.created_by
            : Array.isArray(filters.createdBy)
            ? filters.createdBy
            : [],
        });

        loadedFiltersKeyRef.current = filtersKey;
      } catch (error) {
        console.error("Error fetching protocol filters from API:", error);
        setApiFilters(null);
        loadedFiltersKeyRef.current = null;
      } finally {
        setLoadingFilters(false);
      }
    };

    void loadFilters();
  }, [open, orgId, workspaceId, activeTab, apiFilters]);

  useEffect(() => {
    const wasClosed = !previousOpenRef.current;
    const isOpening = open && wasClosed;

    if (isOpening) {
      setLocalFilters(normalizeInitialFilters(initialFilters));
      setCollapsed(DEFAULT_COLLAPSED);
    }

    previousOpenRef.current = open;
  }, [open, initialFilters]);

  const options = useMemo(() => {
    if (apiFilters) {
      const crops = Array.isArray(apiFilters.crops) ? apiFilters.crops : [];
      const targets = Array.isArray(apiFilters.targets)
        ? apiFilters.targets
        : [];
      const countries = Array.isArray(apiFilters.countries)
        ? apiFilters.countries
        : [];
      const createdBy = Array.isArray(apiFilters.created_by)
        ? apiFilters.created_by
        : Array.isArray(apiFilters.createdBy)
        ? apiFilters.createdBy
        : [];

      return {
        type: DEFAULT_TYPE_OPTIONS,
        crop: [...crops].sort(),
        target: [...targets].sort(),
        country: [...countries].sort(),
        creators: createdBy
          .filter((creator) => creator?.id && creator?.name)
          .map((creator) => ({
            id: String(creator.id),
            name: String(creator.name),
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      };
    }

    const crops = new Set<string>();
    const targets = new Set<string>();
    const countries = new Set<string>();
    const creators = new Map<string, string>();

    for (const record of data ?? []) {
      if (record?.crop) {
        crops.add(String(record.crop));
      }

      if (record?.target) {
        if (Array.isArray(record.target)) {
          record.target.forEach((target: unknown) => {
            if (target) {
              targets.add(String(target));
            }
          });
        } else {
          targets.add(String(record.target));
        }
      }

      if (record?.country) {
        countries.add(String(record.country));
      }

      const creatorId =
        record?.created_by ??
        record?.creator?.id ??
        record?.creator_id ??
        record?.created_by_id;

      const creatorName =
        record?.created_by_name ??
        record?.creator?.name ??
        record?.created_by?.name;

      if (creatorId || creatorName) {
        const id = String(creatorId ?? creatorName);
        creators.set(id, creatorName ? String(creatorName) : id);
      }
    }

    return {
      type: DEFAULT_TYPE_OPTIONS,
      crop: Array.from(crops).sort(),
      target: Array.from(targets).sort(),
      country: Array.from(countries).sort(),
      creators: Array.from(creators.entries())
        .map(([id, name]) => ({
          id,
          name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [apiFilters, data]);

  useEffect(() => {
    if (loadingFilters) return;

    setLocalFilters((previousFilters) => ({
      ...previousFilters,
      status: [],
      type: previousFilters.type.filter((type) => options.type.includes(type)),
      crop: previousFilters.crop.filter((crop) => options.crop.includes(crop)),
      target: previousFilters.target.filter((target) =>
        options.target.includes(target)
      ),
      country: previousFilters.country.filter((country) =>
        options.country.includes(country)
      ),
      creator: previousFilters.creator.filter((id) =>
        options.creators.some((creator) => creator.id === id)
      ),
    }));
  }, [loadingFilters, options]);

  const toggleOption = (
    section: keyof CommonFiltersState,
    value: string
  ): void => {
    setLocalFilters((previousFilters) => {
      const selectedValues = new Set(previousFilters[section] ?? []);

      if (selectedValues.has(value)) {
        selectedValues.delete(value);
      } else {
        selectedValues.add(value);
      }

      return {
        ...previousFilters,
        [section]: Array.from(selectedValues),
      };
    });
  };

  const toggleAll = (
    section: keyof CommonFiltersState,
    allValues: string[]
  ): void => {
    setLocalFilters((previousFilters) => {
      const selectedValues = new Set(previousFilters[section] ?? []);

      const isAllSelected =
        allValues.length > 0 &&
        allValues.every((value) => selectedValues.has(value));

      return {
        ...previousFilters,
        [section]: isAllSelected ? [] : [...allValues],
      };
    });
  };

  const clearSection = (section: keyof CommonFiltersState): void => {
    setLocalFilters((previousFilters) => ({
      ...previousFilters,
      [section]: [],
    }));
  };

  const selectedCount = (section: keyof CommonFiltersState): number =>
    localFilters[section]?.length ?? 0;

  const handleApply = (): void => {
    onApply?.({
      ...localFilters,
      status: [],
    });
    onClose();
  };

  const handleCancel = (): void => {
    setLocalFilters(normalizeInitialFilters(initialFilters));
    onCancel?.();
    onClose();
  };

  if (!open) return null;

  const panel = (
    <div aria-hidden={!open} className="new-filters-panel">
      <div onClick={onClose} className="new-filters-panel__backdrop" />

      <aside
        role="dialog"
        aria-modal="true"
        className="new-filters-panel__drawer"
      >
        <header className="new-filters-panel__header">
          <div className="new-filters-panel__title">Filters</div>

          <button
            type="button"
            aria-label="Close filters"
            onClick={onClose}
            className="new-filters-panel__close-button"
          >
            <CloseIcon />
          </button>
        </header>

        <div className="new-filters-panel__body">
          {loadingFilters ? (
            <FiltersPanelSkeleton sections={5} />
          ) : (
            <>
              {renderSection(
                "Type",
                "type",
                options.type,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              {renderSection(
                "Crop",
                "crop",
                options.crop,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              {renderSection(
                "Country",
                "country",
                options.country,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              {renderSection(
                "Target",
                "target",
                options.target,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              {renderUserSection(
                "Creator",
                "creator",
                options.creators,
                localFilters,
                collapsed,
                setCollapsed,
                selectedCount,
                toggleAll,
                toggleOption,
                clearSection
              )}

              <div className="new-filters-panel__spacer" />
            </>
          )}
        </div>

        <footer className="new-filters-panel__footer">
          <button
            type="button"
            onClick={handleCancel}
            className="new-filters-panel__button new-filters-panel__button--cancel"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleApply}
            className="new-filters-panel__button new-filters-panel__button--apply"
          >
            Apply
          </button>
        </footer>
      </aside>
    </div>
  );

  return createPortal(panel, document.body);
}

function FiltersPanelSkeleton({
  sections = 5,
  optionsPerSection = 4,
}: {
  sections?: number;
  optionsPerSection?: number;
}): JSX.Element {
  return (
    <div className="new-filters-panel__skeleton">
      {Array.from({ length: sections }).map((_, sectionIndex) => (
        <div key={sectionIndex} className="new-filters-panel__skeleton-section">
          <div className="new-filters-panel__skeleton-header">
            <Skeleton.Input active size="small" style={{ width: 110 }} />
            <Skeleton.Button active size="small" shape="circle" />
          </div>

          <div className="new-filters-panel__skeleton-options">
            {Array.from({ length: optionsPerSection }).map((_, optionIndex) => (
              <div
                key={optionIndex}
                className="new-filters-panel__skeleton-option"
              >
                <Skeleton.Avatar
                  active
                  shape="square"
                  size={16}
                  style={{ borderRadius: 2 }}
                />
                <Skeleton.Input active size="small" style={{ width: 160 }} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CollapseArrow({ open }: { open: boolean }): JSX.Element {
  return (
    <div
      className={`new-filters-panel__collapse-arrow ${
        open
          ? "new-filters-panel__collapse-arrow--open"
          : "new-filters-panel__collapse-arrow--closed"
      }`}
      aria-hidden="true"
    >
      <CollapseArrowIcon />
    </div>
  );
}

function FilterBadge({
  label,
  title,
  onClear,
}: {
  label: string;
  title: string;
  onClear: () => void;
}): JSX.Element {
  return (
    <div className="new-filters-panel__badge-wrapper">
      <div className="new-filters-panel__filter-pill">
        <span className="new-filters-panel__filter-pill-label">{label}</span>

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClear();
          }}
          aria-label={`Clear ${title}`}
          className="new-filters-panel__filter-pill-clear"
        >
          <SmallCloseIcon />
        </button>
      </div>
    </div>
  );
}

function renderSection(
  title: string,
  keyName: keyof CommonFiltersState,
  options: string[],
  localFilters: CommonFiltersState,
  collapsed: Record<keyof CommonFiltersState, boolean>,
  setCollapsed: React.Dispatch<
    React.SetStateAction<Record<keyof CommonFiltersState, boolean>>
  >,
  selectedCount: (section: keyof CommonFiltersState) => number,
  toggleAll: (section: keyof CommonFiltersState, values: string[]) => void,
  toggleOption: (section: keyof CommonFiltersState, value: string) => void,
  clearSection: (section: keyof CommonFiltersState) => void
): JSX.Element {
  const openState = !collapsed[keyName];
  const count = selectedCount(keyName);
  const selectedValues = localFilters[keyName] ?? [];

  const isAllChecked =
    options.length > 0 &&
    options.every((option) => selectedValues.includes(option));

  const shouldShowCountBadge = isAllChecked || count > 0;
  const countBadgeLabel = isAllChecked ? "All" : `(${count})`;

  const toggleCollapsed = (): void => {
    setCollapsed((currentCollapsed) => ({
      ...currentCollapsed,
      [keyName]: !currentCollapsed[keyName],
    }));
  };

  return (
    <div key={String(keyName)} className="new-filters-panel__section">
      <div
        role="button"
        tabIndex={0}
        onClick={toggleCollapsed}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleCollapsed();
          }
        }}
        aria-expanded={openState}
        aria-label={`${openState ? "Collapse" : "Expand"} ${title}`}
        className="new-filters-panel__section-header"
      >
        <div className="new-filters-panel__section-title-wrapper">
          <div className="new-filters-panel__section-title">{title}</div>

          {shouldShowCountBadge && (
            <FilterBadge
              label={countBadgeLabel}
              title={title}
              onClear={() => {
                clearSection(keyName);
              }}
            />
          )}
        </div>

        <CollapseArrow open={openState} />
      </div>

      {openState && (
        <div>
          {options.length > 0 && (
            <div
              onClick={() => {
                toggleAll(keyName, options);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleAll(keyName, options);
                }
              }}
              className="new-filters-panel__all-option-row"
            >
              <Square checked={isAllChecked} variant="all" />

              <div className="new-filters-panel__option-text">
                {getAllLabel(title)}
              </div>
            </div>
          )}

          <div className="new-filters-panel__options-list">
            {options.map((option) => {
              const checked = selectedValues.includes(option);

              return (
                <div
                  key={option}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    toggleOption(keyName, option);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      toggleOption(keyName, option);
                    }
                  }}
                  className="new-filters-panel__option-row"
                >
                  <Square checked={checked} />

                  <div className="new-filters-panel__option-text">{option}</div>
                </div>
              );
            })}

            {options.length === 0 && (
              <div className="new-filters-panel__no-options">No options</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function renderUserSection(
  title: string,
  keyName: keyof CommonFiltersState,
  options: Array<{ id: string; name: string }>,
  localFilters: CommonFiltersState,
  collapsed: Record<keyof CommonFiltersState, boolean>,
  setCollapsed: React.Dispatch<
    React.SetStateAction<Record<keyof CommonFiltersState, boolean>>
  >,
  selectedCount: (section: keyof CommonFiltersState) => number,
  toggleAll: (section: keyof CommonFiltersState, values: string[]) => void,
  toggleOption: (section: keyof CommonFiltersState, value: string) => void,
  clearSection: (section: keyof CommonFiltersState) => void
): JSX.Element {
  const ids = options.map((option) => option.id);
  const openState = !collapsed[keyName];
  const count = selectedCount(keyName);
  const selectedValues = localFilters[keyName] ?? [];

  const isAllChecked =
    ids.length > 0 && ids.every((id) => selectedValues.includes(id));

  const shouldShowCountBadge = isAllChecked || count > 0;
  const countBadgeLabel = isAllChecked ? "All" : `(${count})`;

  const toggleCollapsed = (): void => {
    setCollapsed((currentCollapsed) => ({
      ...currentCollapsed,
      [keyName]: !currentCollapsed[keyName],
    }));
  };

  return (
    <div key={String(keyName)} className="new-filters-panel__section">
      <div
        role="button"
        tabIndex={0}
        onClick={toggleCollapsed}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleCollapsed();
          }
        }}
        aria-expanded={openState}
        aria-label={`${openState ? "Collapse" : "Expand"} ${title}`}
        className="new-filters-panel__section-header"
      >
        <div className="new-filters-panel__section-title-wrapper">
          <div className="new-filters-panel__section-title">{title}</div>

          {shouldShowCountBadge && (
            <FilterBadge
              label={countBadgeLabel}
              title={title}
              onClear={() => {
                clearSection(keyName);
              }}
            />
          )}
        </div>

        <CollapseArrow open={openState} />
      </div>

      {openState && (
        <div>
          {options.length > 0 && (
            <div
              onClick={() => {
                toggleAll(keyName, ids);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleAll(keyName, ids);
                }
              }}
              className="new-filters-panel__all-option-row"
            >
              <Square checked={isAllChecked} variant="all" />

              <div className="new-filters-panel__option-text">
                {getAllLabel(title)}
              </div>
            </div>
          )}

          <div className="new-filters-panel__options-list">
            {options.map((option) => {
              const checked = selectedValues.includes(option.id);

              return (
                <div
                  key={option.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    toggleOption(keyName, option.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      toggleOption(keyName, option.id);
                    }
                  }}
                  className="new-filters-panel__option-row"
                >
                  <Square checked={checked} />

                  <div className="new-filters-panel__option-text">
                    {option.name}
                  </div>
                </div>
              );
            })}

            {options.length === 0 && (
              <div className="new-filters-panel__no-options">No options</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Square({
  checked,
  variant = "default",
}: {
  checked: boolean;
  variant?: "default" | "all";
}): JSX.Element {
  const squareClassName = checked
    ? variant === "all"
      ? "new-filters-panel__square new-filters-panel__square--all-checked"
      : "new-filters-panel__square new-filters-panel__square--checked"
    : "new-filters-panel__square new-filters-panel__square--unchecked";

  return (
    <div className="new-filters-panel__square-wrapper">
      <div className={squareClassName}>
        {checked ? (
          variant === "all" ? (
            <div className="new-filters-panel__all-inner-square" />
          ) : (
            <CheckIcon />
          )
        ) : null}
      </div>
    </div>
  );
}
